// src/pages/admin/CourseLectures/CourseLecturesLayout.tsx
import React, { useState, useMemo, useEffect } from "react";
import {
  FaBookOpen,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaExpand,
  FaCompress,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";
import { COURSES, Course, Lecture } from "./data/coursesData";
import { LectureProgress } from "./components/LectureUI";

import Lecture01Vorbereitung from "./lectures/Lecture01Vorbereitung";
import Lecture02Lebenslauf from "./lectures/Lecture02Lebenslauf";
import Lecture03Stellenausschreibung from "./lectures/Lecture03Stellenausschreibung";
import Lecture04AnschreibenDIN5008 from "./lectures/Lecture04AnschreibenDIN5008";
import Lecture05Unterlagenkontrolle from "./lectures/Lecture05Unterlagenkontrolle";
import Lecture06ErfolgreichBewerben from "./lectures/Lecture06ErfolgreichBewerben";
import Lecture07ApplicationTracker from "./lectures/Lecture07ApplicationTracker";
import Lecture08AusbildungSuche from "./lectures/Lecture08AusbildungSuche";
import Lecture09ApplicationReview from "./lectures/Lecture09ApplicationReview";
import Lecture10Unterkunft from "./lectures/Lecture10Unterkunft";
import AuPairLecture01Introduction from "./lectures/Au Pair/AuPairLecture01Introduction";
import AuPairLecture02CultureProfile from "./lectures/Au Pair/AuPairLecture02CultureProfile";
import Aupairlecture03hostfamilysearch from "./lectures/Au Pair/Aupairlecture03hostfamilysearch";
import FSJLecture01Introduction from "./lectures/Fsj_Bfd/FSJLecture01Introduction";
import FSJLecture03Application from "./lectures/Fsj_Bfd/FSJLecture03Application";

const LECTURE_COMPONENTS: Record<string, React.FC> = {
  Lecture01Vorbereitung,
  Lecture02Lebenslauf,
  Lecture03Stellenausschreibung,
  Lecture04AnschreibenDIN5008,
  Lecture05Unterlagenkontrolle,
  Lecture06ErfolgreichBewerben,
  Lecture07ApplicationTracker,
  Lecture08AusbildungSuche,
  Lecture09ApplicationReview,
  Lecture10Unterkunft,
  AuPairLecture01Introduction,
  AuPairLecture02CultureProfile,
  Aupairlecture03hostfamilysearch,
  FSJLecture01Introduction,
  FSJLecture03Application,
  // Lecture03Bewerbungsschreiben, ← add here as you build them
};

// ── Coming Soon Placeholder ───────────────────────────────────────
const ComingSoonPlaceholder: React.FC<{ lecture: Lecture }> = ({ lecture }) => (
  <div className="flex flex-col items-center justify-center min-h-[300px] text-center py-12">
    <div className="w-16 h-16 rounded-2xl bg-blue-100 text-[#009fe3] flex items-center justify-center text-2xl mb-4">
      📄
    </div>
    <h2 className="text-xl font-bold text-gray-700 mb-2">
      {lecture.num} – {lecture.title}
    </h2>
    <p className="text-gray-400 text-sm max-w-xs">
      Diese Lektion wird bald verfügbar sein. Der Inhalt wird gerade
      vorbereitet.
    </p>
    <div className="mt-4 px-4 py-2 bg-blue-50 text-[#009fe3] text-xs font-bold rounded-full border border-blue-200">
      🚧 Coming Soon
    </div>
  </div>
);

// ── Sidebar content (reused in drawer + desktop panel) ────────────
const SidebarContent: React.FC<{
  selectedCourseId: string;
  selectedCourse: Course;
  selectedLectureId: string;
  onCourseChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onLectureSelect: (id: string) => void;
}> = ({
  selectedCourseId,
  selectedCourse,
  selectedLectureId,
  onCourseChange,
  onLectureSelect,
}) => (
  <div className="flex flex-col h-full">
    {/* Course selector */}
    <div className="px-3.5 pt-3.5 pb-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
        📚 Kurs auswählen
      </label>
      <div className="relative">
        <select
          value={selectedCourseId}
          onChange={onCourseChange}
          className="w-full appearance-none pl-3 pr-8 py-2.5 border-2 border-[#009fe3] rounded-lg text-sm font-semibold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#009fe3]/20 cursor-pointer shadow-sm"
        >
          {COURSES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <FaChevronDown
          size={10}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#009fe3]"
        />
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="text-[11px] font-bold text-[#0369a1] bg-blue-100 px-2 py-0.5 rounded-full">
          {selectedCourse.lectures.length} Lektionen
        </span>
        <span className="text-[11px] text-gray-400">
          {selectedCourse.description}
        </span>
      </div>
    </div>

    {/* Lecture list header */}
    <div className="flex items-center justify-between px-3.5 py-2 bg-white border-b border-gray-100 flex-shrink-0">
      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
        Lektionen
      </span>
      <span className="text-[10px] font-bold text-white bg-[#009fe3] px-2 py-0.5 rounded-full">
        {selectedCourse.lectures.length}
      </span>
    </div>

    {/* Lecture list */}
    <div className="overflow-y-auto flex-1 py-1">
      {selectedCourse.lectures.map((lecture) => {
        const isActive = lecture.id === selectedLectureId;
        const isBuilt = !!LECTURE_COMPONENTS[lecture.component];
        return (
          <button
            key={lecture.id}
            onClick={() => onLectureSelect(lecture.id)}
            className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left border-l-[3px] transition-all ${
              isActive
                ? "bg-blue-50 border-[#009fe3]"
                : "border-transparent hover:bg-gray-50 hover:border-blue-200"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                isActive
                  ? "bg-[#009fe3] text-white"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {lecture.num}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`text-sm font-semibold truncate ${isActive ? "text-[#0369a1]" : "text-gray-800"}`}
              >
                {lecture.title}
              </div>
              <div className="text-[11px] text-gray-400 truncate">
                {lecture.subtitle}
              </div>
            </div>
            {!isBuilt && (
              <span className="text-[9px] font-bold text-gray-300 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">
                BALD
              </span>
            )}
            {isActive && (
              <FaChevronRight
                size={10}
                className="text-[#009fe3] flex-shrink-0"
              />
            )}
          </button>
        );
      })}
    </div>
  </div>
);

// ── Main Layout ───────────────────────────────────────────────────
const CourseLecturesLayout: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [presentMode, setPresentMode] = useState(false);

  const [selectedCourseId, setSelectedCourseId] = useState<string>(
    COURSES[0].id,
  );
  const [selectedLectureId, setSelectedLectureId] = useState<string>(
    COURSES[0].lectures[0]?.id ?? "",
  );

  // Close drawer when resizing to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setDrawerOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const selectedCourse: Course = useMemo(
    () => COURSES.find((c) => c.id === selectedCourseId) ?? COURSES[0],
    [selectedCourseId],
  );

  const selectedLecture: Lecture | undefined = useMemo(
    () => selectedCourse.lectures.find((l) => l.id === selectedLectureId),
    [selectedCourse, selectedLectureId],
  );

  const currentIndex = selectedCourse.lectures.findIndex(
    (l) => l.id === selectedLectureId,
  );
  const prevLecture =
    currentIndex > 0 ? selectedCourse.lectures[currentIndex - 1] : null;
  const nextLecture =
    currentIndex < selectedCourse.lectures.length - 1
      ? selectedCourse.lectures[currentIndex + 1]
      : null;

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const courseId = e.target.value;
    const course = COURSES.find((c) => c.id === courseId);
    setSelectedCourseId(courseId);
    setSelectedLectureId(course?.lectures[0]?.id ?? "");
  };

  const handleLectureSelect = (id: string) => {
    setSelectedLectureId(id);
    setDrawerOpen(false); // auto-close drawer on mobile after picking
  };

  const LectureComponent =
    selectedLecture && LECTURE_COMPONENTS[selectedLecture.component]
      ? LECTURE_COMPONENTS[selectedLecture.component]
      : null;

  const sidebarProps = {
    selectedCourseId,
    selectedCourse,
    selectedLectureId,
    onCourseChange: handleCourseChange,
    onLectureSelect: handleLectureSelect,
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* ── HEADER ── */}
      <PageHeader
        icon={<FaBookOpen />}
        title="Course Lectures"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Course Lectures" },
        ]}
      />

      {/* Toolbar: mobile drawer toggle + present mode + mobile lecture pill */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-200 px-4 sm:px-6 py-2 flex items-center justify-between gap-3">
        <button
          onClick={() => setDrawerOpen(true)}
          className="md:hidden flex-shrink-0 w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center justify-center transition-colors"
          aria-label="Lektionen öffnen"
        >
          <FaBars size={16} />
        </button>
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
          <button
            onClick={() => setPresentMode((v) => !v)}
            title={presentMode ? "Exit present mode" : "Present mode"}
            className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors"
          >
            {presentMode ? <FaCompress size={12} /> : <FaExpand size={12} />}
            <span className="hidden sm:inline">{presentMode ? "Exit" : "Present"}</span>
          </button>
          {selectedLecture && (
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-colors max-w-[140px]"
            >
              <FaBookOpen size={10} className="flex-shrink-0" />
              <span className="truncate">
                {selectedLecture.num} {selectedLecture.title}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ── MOBILE DRAWER ── */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 md:hidden flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Panel */}
          <div className="relative w-[300px] max-w-[85vw] bg-white h-full flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#009fe3] to-[#0088cc] flex-shrink-0">
              <span className="text-white font-bold text-sm">
                Lektionen auswählen
              </span>
              <button
                onClick={() => setDrawerOpen(false)}
                className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
              >
                <FaTimes size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <SidebarContent {...sidebarProps} />
            </div>
          </div>
        </div>
      )}

      {/* ── BODY ── */}
      <div className="flex flex-1 min-h-0 md:h-[calc(100vh-56px)]">
        {/* Desktop sidebar — hidden in present mode */}
        {!presentMode && (
          <aside className="hidden md:flex w-72 min-w-[272px] bg-white border-r border-gray-200 flex-col overflow-hidden flex-shrink-0">
            <SidebarContent {...sidebarProps} />
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 min-w-0">
          <div className={`mx-auto px-4 sm:px-6 py-4 sm:py-6 ${presentMode ? "max-w-5xl text-lg" : "max-w-3xl"}`}>
            {selectedLecture ? (
              <>
                {/* Lecture header */}
                <div className="mb-4">
                  <div className="inline-flex items-center gap-1.5 bg-blue-100 text-[#0369a1] text-[11px] font-bold px-2.5 py-1 rounded-full mb-2 uppercase tracking-wide max-w-full overflow-hidden">
                    <FaBookOpen size={10} className="flex-shrink-0" />
                    <span className="truncate">
                      {selectedCourse.title} · Lektion {selectedLecture.num}
                    </span>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900 leading-tight">
                        {selectedLecture.title}
                      </h1>
                      <p className="text-sm text-gray-400 mt-1">
                        {selectedLecture.subtitle}
                      </p>
                    </div>
                    {/* Prev/Next — desktop only */}
                    <div className="hidden sm:flex gap-2 flex-shrink-0">
                      <button
                        disabled={!prevLecture}
                        onClick={() =>
                          prevLecture && setSelectedLectureId(prevLecture.id)
                        }
                        className="px-3 py-1.5 rounded-lg border border-[#009fe3] text-[#009fe3] text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-50 transition-colors"
                      >
                        ← Zurück
                      </button>
                      <button
                        disabled={!nextLecture}
                        onClick={() =>
                          nextLecture && setSelectedLectureId(nextLecture.id)
                        }
                        className="px-3 py-1.5 rounded-lg bg-[#009fe3] text-white text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0088cc] transition-colors"
                      >
                        Weiter →
                      </button>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <LectureProgress
                  current={currentIndex + 1}
                  total={selectedCourse.lectures.length}
                />

                {/* Lecture content */}
                {LectureComponent ? (
                  <LectureComponent />
                ) : (
                  <ComingSoonPlaceholder lecture={selectedLecture} />
                )}

                {/* Bottom nav — full width on mobile, auto on desktop */}
                <div className="flex gap-3 mt-6 mb-10">
                  <button
                    disabled={!prevLecture}
                    onClick={() =>
                      prevLecture && setSelectedLectureId(prevLecture.id)
                    }
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg border border-[#009fe3] text-[#009fe3] text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-50 transition-colors min-w-0"
                  >
                    <span className="flex-shrink-0">←</span>
                    <span className="truncate">
                      {prevLecture
                        ? `${prevLecture.num} ${prevLecture.title}`
                        : "Zurück"}
                    </span>
                  </button>
                  <button
                    disabled={!nextLecture}
                    onClick={() =>
                      nextLecture && setSelectedLectureId(nextLecture.id)
                    }
                    className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#009fe3] text-white text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#0088cc] transition-colors min-w-0"
                  >
                    <span className="truncate">
                      {nextLecture
                        ? `${nextLecture.num} ${nextLecture.title}`
                        : "Weiter"}
                    </span>
                    <span className="flex-shrink-0">→</span>
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-20 text-gray-400">
                Bitte wähle eine Lektion aus der Seitenleiste.
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default CourseLecturesLayout;
